import UiLanguageSelect from "../components/UiLanguageSelect";
import { useTranslation } from "react-i18next";
// src/pages/ResetPassword.tsx
import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { apiPost } from "../api/client";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useParams } from "react-router-dom";
import { companyRoute } from "../utils/routeHelpers";
import { A } from "../styles/appleClasses";
import { Button } from "../components/Button";

type ResetOutcome =
  | { kind: "local"; code: "passwordMismatch" | "resetFailed" }
  | { kind: "known"; key: "resetPassword.invalidOrExpiredToken" | "changePassword.passwordTooShort" | "accountInlays.errors.tooManyRequests" }
  | { kind: "raw"; value: React.ReactNode };

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<"success" | null>(null);
  const [error, setError] = useState<ResetOutcome | null>(null);
  const [loading, setLoading] = useState(false);

  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMsg(null);
    if (password !== confirm) {
      setError({ kind: "local", code: "passwordMismatch" });
      return;
    }
    setLoading(true);
    try {
      await apiPost("/auth/reset-password", { token, new_password: password });
      setMsg("success");
      setPassword("");
      setConfirm("");
      setTimeout(() => navigate(companyRoute(company_id, "/login")), 2000);
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 400 && detail === "Invalid or expired token.") setError({ kind: "known", key: "resetPassword.invalidOrExpiredToken" });
      else if (status === 400 && detail === "Password too short.") setError({ kind: "known", key: "changePassword.passwordTooShort" });
      else if (status === 429 && detail === "Too many requests. Please try again later.") setError({ kind: "known", key: "accountInlays.errors.tooManyRequests" });
      else setError(detail ? { kind: "raw", value: detail } : { kind: "local", code: "resetFailed" });
    } finally {
      setLoading(false);
    }
  };

  const errorText = error?.kind === "raw" ? error.value
    : error?.kind === "known" ? t(error.key)
    : error?.code === "passwordMismatch" ? t("register.passwordMismatch")
    : error?.code === "resetFailed" ? t("resetPassword.resetFailed") : "";

  const iconBtn = `${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;
  const alertBase = `mt-4 rounded-2xl border px-4 py-3 ${A.body} text-center`;

  if (!token)
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-3 py-10`}>
        <div className={`${A.card} w-full max-w-md p-6 sm:p-8 text-center`}>
          <div className="mb-4 flex justify-end"><UiLanguageSelect /></div>
          <div className={`${A.title} text-red-600 mb-2`}>
            {t("resetPassword.missingToken")}
          </div>
          <div className={A.label}>
            {t("resetPassword.requestNewLink")}
          </div>
          <div className="mt-4">
            <Link to="/forgot-password" className="text-appleblue hover:underline">
              {t("resetPassword.backToForgotPassword")}
            </Link>
          </div>
        </div>
      </div>
    );

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-3 py-10`}>
      <div className={`${A.card} w-full max-w-md p-6 sm:p-8 relative`}>
        {/* X close button */}
        <button
          onClick={() => navigate(companyRoute(company_id, "/login"))}
          className={`${iconBtn} absolute top-3 right-3 p-2`}
          title={t("register.backToLogin")}
          aria-label={t("directory.common.close")}
          type="button"
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-700 transition" />
        </button>

        <div className="mb-4 flex justify-end pr-8"><UiLanguageSelect /></div>
        <div className="text-center mb-6">
          <h2 className={A.title}>{t("resetPassword.title")}</h2>
          <div className={`mt-1 ${A.label}`}>{t("resetPassword.explanation")}</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block mb-1 ${A.label}`}>{t("resetPassword.newPassword")}</label>
            <input
              type="password"
              className={A.input}
              placeholder={t("resetPassword.newPassword")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div>
            <label className={`block mb-1 ${A.label}`}>{t("resetPassword.confirmPassword")}</label>
            <input
              type="password"
              className={A.input}
              placeholder={t("resetPassword.confirmPassword")}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>

          {/* styling only: swap to shared Button */}
          <Button
            className="w-full justify-center"
            variant="primary"
            type="submit"
            disabled={loading}
          >
            {loading ? t("resetPassword.resettingDots") : t("resetPassword.title")}
          </Button>
        </form>

        {msg && (
          <div className={`${alertBase} border-green-200 bg-green-50 text-green-700`}>
            {t("resetPassword.success")}
          </div>
        )}
        {errorText && (
          <div className={`${alertBase} border-red-200 bg-red-50 text-red-700`}>
            {errorText}
          </div>
        )}

        <div className={`text-center mt-6 ${A.label}`}>
          <Link
            to={companyRoute(company_id, "/login")}
            className="text-appleblue hover:underline"
          >
            {t("register.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
